import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { handleToolError, maybeShow3DayOffer } from "@/lib/api";
import { ThreeDayOfferModal } from "@/components/ThreeDayOfferModal";
import { TrialLimitModal } from "@/components/TrialLimitModal";
import { OneAnalysisLeft } from "@/components/OneAnalysisLeft";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TrafficLight } from "@/components/TrafficLight";
import { formatRupiah, CATEGORY_LABEL } from "@/lib/format";
import { GitCompare, X, Plus, Trophy, Loader2, Info, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = [
  ["paling_hemat", "Paling hemat"], ["paling_kuat", "Paling kuat"], ["paling_jauh", "Paling jauh"],
  ["paling_ringan", "Paling ringan"], ["paling_cepat_charge", "Paling cepat di-charge"],
  ["tanpa_sewa_baterai", "Tanpa sewa baterai"], ["cocok_boncengan", "Cocok untuk boncengan"],
];

const ROWS = [
  ["Kategori", "category", "cat"], ["Harga resmi", "official_price", "rp", "min"], ["Daya puncak", "motor_peak_w", "w", "max"],
  ["Torsi motor", "motor_torque_nm", "nm", "max"], ["Kapasitas baterai", "battery_capacity_kwh", "kwh", "max"],
  ["Skema baterai", "battery_scheme", "scheme"], ["Sewa baterai/bulan", "monthly_battery_rental", "rental", "min"],
  ["Jarak klaim", "claimed_range_km", "km", "max"], ["Konsumsi", "efficiency_wh_per_km", "whkm", "min"],
  ["Waktu pengisian", "charge_time_hours", "jam", "min"], ["Kecepatan maks", "top_speed_kmh", "kmh", "max"],
  ["Berat", "curb_weight_kg", "kg", "min"], ["Beban maks", "maximum_payload_kg", "kg", "max"],
  ["Baterai dilepas", "removable_battery", "bool"], ["Garansi", "vehicle_warranty", "txt"],
];

const TERMS = { motor_peak_w: "Tenaga sesaat maksimum motor. Makin besar biasanya makin kuat menanjak.", efficiency_wh_per_km: "Konsumsi energi per km. Makin kecil makin hemat.", battery_capacity_kwh: "Kapasitas simpan energi baterai. Makin besar biasanya jarak makin jauh." };

export default function Compare() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState([]);
  const [priority, setPriority] = useState("");
  const [result, setResult] = useState(null);
  const [credit, setCredit] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/vehicles", { params: { category: "motor_listrik" } }),
      api.get("/vehicles", { params: { category: "sepeda_listrik" } }),
    ]).then(([m, b]) => {
      const list = [...m.data.items, ...b.data.items];
      setAll(list);
      const add = params.get("add");
      if (add && list.some((v) => v.vehicle_id === add)) setSelected([add]);
    }).catch(() => {});
  }, []);

  const addVehicle = (id) => { if (selected.length < 3 && !selected.includes(id)) setSelected([...selected, id]); };
  const removeVehicle = (id) => { setSelected(selected.filter((x) => x !== id)); setResult(null); };

  const submit = async () => {
    if (selected.length < 2) { toast.error("Pilih minimal 2 kendaraan."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/analysis/compare", { vehicle_ids: selected, priority: priority || null });
      setResult(data);
      maybeShow3DayOffer(data.credit);
      setCredit(data.credit);
      await checkAuth();
      toast.success("Perbandingan berhasil dibuat!");
    } catch (err) {
      handleToolError(err, navigate);
    } finally { setLoading(false); }
  };

  const vehicles = result ? result.vehicles : selected.map((id) => all.find((v) => v.vehicle_id === id)).filter(Boolean);

  const bestIndex = (key, mode) => {
    if (!mode || !result) return -1;
    const vals = vehicles.map((v) => v[key]);
    const valid = vals.filter((x) => x != null && x !== "");
    if (!valid.length) return -1;
    const target = mode === "min" ? Math.min(...valid) : Math.max(...valid);
    return vals.findIndex((x) => x === target);
  };

  const fmt = (v, key, type) => {
    const val = v[key];
    if (type === "cat") return CATEGORY_LABEL[val] || val;
    if (val == null || val === "") return "Data belum tersedia";
    if (type === "rp" || type === "rental") return type === "rental" && val === 0 ? "Tidak ada" : formatRupiah(val);
    if (type === "bool") return val ? "Ya" : "Tidak";
    if (type === "scheme") return { beli: "Beli", sewa: "Sewa", swap: "Swap" }[val] || val;
    const unit = { w: " W", nm: " Nm", kwh: " kWh", km: " km", whkm: " Wh/km", jam: " jam", kmh: " km/jam", kg: " kg" }[type] || "";
    return `${val}${unit}`;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="compare-page">
      <TrialLimitModal />
      <ThreeDayOfferModal />
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-navy mb-2 flex items-center gap-3"><GitCompare className="w-8 h-8 text-teal" />Bandingkan Kendaraan</h1>
      <p className="text-muted-foreground mb-8">Pilih 2 sampai 3 kendaraan listrik untuk dibandingkan berdampingan.</p>

      {/* Selector */}
      <Card className="rounded-2xl p-5 mb-6">
        <div className="flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <label className="text-sm text-muted-foreground">Tambah kendaraan ({selected.length}/3)</label>
            <Combobox testId="compare-add-select" value="" onChange={addVehicle} disabled={selected.length >= 3} className="mt-1"
              placeholder="Pilih kendaraan…" searchPlaceholder="Cari kendaraan…"
              options={all.filter((v) => !selected.includes(v.vehicle_id)).map((v) => ({ value: v.vehicle_id, label: `${v.brand} ${v.model}${v.variant ? ` ${v.variant}` : ""}` }))} />
          </div>
          <div className="flex-1">
            <label className="text-sm text-muted-foreground">Prioritas Anda (opsional)</label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="compare-priority" className="rounded-xl h-11 mt-1"><SelectValue placeholder="Mana yang lebih cocok untuk saya?" /></SelectTrigger>
              <SelectContent className="bg-white">{PRIORITIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button data-testid="compare-submit" onClick={submit} disabled={loading || selected.length < 2} className="rounded-full bg-teal hover:bg-teal-hover h-11 px-6">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Bandingkan Sekarang"}
          </Button>
        </div>
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {selected.map((id) => { const v = all.find((x) => x.vehicle_id === id); return v && (
              <span key={id} className="inline-flex items-center gap-2 bg-accent text-teal text-sm font-medium px-3 py-1.5 rounded-full">
                {v.brand} {v.model}
                <button data-testid={`compare-remove-${id}`} onClick={() => removeVehicle(id)}><X className="w-3.5 h-3.5" /></button>
              </span>
            ); })}
          </div>
        )}
      </Card>

      {result?.recommendation && (
        <Card className="rounded-2xl p-5 mb-6 bg-navy text-white flex items-start gap-3" data-testid="compare-recommendation">
          <Trophy className="w-6 h-6 text-amber-400 flex-shrink-0" />
          <div><p className="font-heading font-bold text-lg">Rekomendasi: {result.recommendation.name}</p><p className="text-white/70 text-sm">{result.recommendation.reason}</p></div>
        </Card>
      )}

      {vehicles.length >= 2 ? (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white" data-testid="compare-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-4 text-muted-foreground font-medium sticky left-0 bg-white">Spesifikasi</th>
                {vehicles.map((v) => (
                  <th key={v.vehicle_id} className="p-4 text-center min-w-[160px]">
                    {v.image && <img src={v.image} alt={v.model} className="w-20 h-20 object-cover rounded-xl mx-auto mb-2" />}
                    <p className="font-heading font-bold text-navy">{v.model}</p>
                    <p className="text-xs text-muted-foreground">{v.brand}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, key, type, mode]) => {
                const best = bestIndex(key, mode);
                return (
                  <tr key={key} className="border-b border-border last:border-0">
                    <td className="p-4 text-muted-foreground sticky left-0 bg-white">
                      <span className="inline-flex items-center gap-1">
                        {label}
                        {TERMS[key] && (
                          <TooltipProvider><Tooltip><TooltipTrigger><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent className="max-w-[200px] bg-navy text-white">{TERMS[key]}</TooltipContent></Tooltip></TooltipProvider>
                        )}
                      </span>
                    </td>
                    {vehicles.map((v, i) => (
                      <td key={v.vehicle_id} className={`p-4 text-center font-medium ${i === best ? "bg-accent text-teal font-bold" : "text-navy"}`}>
                        {fmt(v, key, type)}
                        {i === best && <Sparkles className="w-3.5 h-3.5 inline ml-1" />}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <Card className="rounded-2xl p-12 text-center text-muted-foreground" data-testid="compare-empty">
          <GitCompare className="w-12 h-12 mx-auto mb-3 opacity-40" />
          Pilih minimal 2 kendaraan untuk mulai membandingkan.
        </Card>
      )}
      {result && credit?.remaining === 1 && <div className="mb-4"><OneAnalysisLeft to="/hitung" label="Lanjutkan Analisis" /></div>}
      {result && <p className="text-xs text-muted-foreground mt-4">Highlight menandai nilai terbaik per baris. Kami tidak membuat skor total tersembunyi — rekomendasi hanya berdasarkan prioritas yang Anda pilih. Hasil estimasi berdasarkan DATA DEMO.</p>}
    </div>
  );
}
